import React, { useState, useEffect } from "react";
import { connect } from "react-redux";
import { get } from "lodash";
import { NotificationManager } from "react-notifications";

import PaginatedTable from "../../../../sharedComponents/tableWithPagination";
import AddMeasureForm from "./addMeasureForm.js";
import {
	getIngredientWeights,
	addIngredientWeights,
	updateIngredientWeights,
	deleteIngredientWeights,
} from "../../../../store/actions";
import FilterPanel, { PanelBody } from "../../../../sharedComponents/filterPanel";
import Tooltip from "../../../../sharedComponents/tooltip";
import ConfirmDelete from "../../../../sharedComponents/confirmDelete";

const CommonWeightsAndMeasures = ({
	foodId,
	initialValues,
	getIngredientWeights,
	addIngredientWeights,
	updateIngredientWeights,
	deleteIngredientWeights,
	setActiveTab,
	ingredientWeightList,
	saveAndPublish,
	isView
}) => {
	useEffect(() => {
		foodId && getIngredientWeights(foodId);
	}, [foodId, getIngredientWeights]);

	const columns = isView
		? [
			{
				Header: "Seq",
				accessor: "seq"
			},
			{
				Header: "Quantity",
				accessor: "quantity"
			},
			{
				Header: "Measure",
				accessor: "measure"
			},
			{
				Header: "Grams",
				accessor: "grams"
			}
		]
		: [
			{
				Header: "Seq",
				accessor: "seq"
			},
			{
				Header: "Quantity",
				accessor: "quantity"
			},
			{
				Header: "Measure",
				accessor: "measure"
			},
			{
				Header: "Grams",
				accessor: "grams"
			},
			{
				Header: "Actions",
				accessor: "id",
				className: "text-right",
				headerClassName: "text-right",
				width: 100,
				Cell: ({ original }) => {
					return !original.usda ? (
						<span>
							<Tooltip content="Edit" placement="left">
								<button
									className="btn btn-success btn-ghost btn-sm btn-cell mr-2"
									type="button"
									onClick={() => {
										toggleAddEditMeasurePanel(true);
										setSelectedMeasure(original);
									}}
									disabled={original.usda || !original.id}>
									<i className="icon icon-edit" />
								</button>
							</Tooltip>
							<Tooltip content="Delete" placement="left">
								<button
									className="btn btn-danger btn-ghost btn-sm btn-cell"
									type="button"
									onClick={() => confirmDelete(original)}>
									<i className="icon icon-trash" />
								</button>
							</Tooltip>
						</span>
					) : (
						<span>
							<Tooltip content="Not Allowed" placement="left">
								<i className="icon icon-close small" />
							</Tooltip>
						</span>
					);
				}
			}
		];

	const [submitting, setSubmitting] = useState(false);
	const [list, updateList] = useState([]);
	const [selectedMeasure, setSelectedMeasure] = useState(null);

	useEffect(() => {
		updateList(ingredientWeightList);
	}, [ingredientWeightList, updateList]);

	const addToList = (values) => {
		const isExists = list.filter((item) => item.measure === values.measure);
		values.seq = values.sequence;
		if (isExists && isExists.length) {
			NotificationManager.warning("Item exists in list", "", 5000);
		} else {
			updateList([...list, values]);
		}
		setSubmitting(false);
	};

	const addItem = () => {
		setSubmitting(true);
		const filteredList = list.filter((item) => !item.id);
		const processedList = filteredList.map((item) => ({ ...item, usda: false, food: foodId }));
		if (filteredList && filteredList.length) {
			addIngredientWeights({ id: foodId, data: processedList }, afterAddItem);
		} else {
			afterAddItem();
			setSubmitting(false);
		}
	};

	const afterAddItem = () => {
		setSubmitting(false);
		setActiveTab(0);
		getIngredientWeights(foodId);
		saveAndPublish();
	};

	const editMeasure = (values) => {
		updateIngredientWeights({ food: foodId, ...values }, () => {
			toggleAddEditMeasurePanel(false);
			getIngredientWeights(foodId);
		});
	};

	const [deleteItem, setDeleteItem] = useState(null);
	const confirmDelete = (original) => {
		setDeleteItem(original);
	};
	const confirmEvent = (yes) => {
		yes && deleteMeasure(deleteItem);
		setDeleteItem(null);
	};
	const deleteMeasure = (original) => {
		const successHandler = () => updateList(list.filter((item) => item.measure !== original.measure));
		if (original.id) {
			deleteIngredientWeights({ food: foodId, id: original.id }, successHandler);
		} else {
			successHandler();
		}
	};

	const [showAddEditMeasurePanel, toggleAddEditMeasurePanel] = useState(false);

	const match = window.location.href;
	const isApproval = match.indexOf("/approval") > -1 ? true : false;

	return initialValues && initialValues.id ? (
		<div className="row p-4">
			<div className="col-12">
				<div className="d-flex align-items-center">
					<h4 className="flex-fill m-0">Common Weights and Measures Listing</h4>
					{
						!isView ?
							<button
								type="button"
								onClick={() => {
									setSelectedMeasure(null);
									toggleAddEditMeasurePanel(true);
								}}
								className="btn btn-primary  text-uppercase">
								<i className="icon icon-plus" /> Add new
							</button>
							:
							null
					}
				</div>
				<PaginatedTable
					data={list}
					columns={columns}
					noEmpty={true}
					emptyMessage="At least one measurement is required"
				/>
			</div>
			{
				!isView &&
				<div className="col-12">
					<div className="text-right mt-4">
						<button
							className="btn btn-primary-dark"
							type="button"
							disabled={!list.length || submitting}
							onClick={addItem}>
							Save &amp; {isApproval ? "Approve" : "Close"}
						</button>
					</div>
				</div>
			}

			<ConfirmDelete item={deleteItem} message={<div>Are you sure you want to delete?</div>} onEvent={confirmEvent} />
			<FilterPanel
				className="add-category-panel"
				show={showAddEditMeasurePanel}
				toggle={() => toggleAddEditMeasurePanel(!showAddEditMeasurePanel)}
				title="Add Common Weights and Measures"
				closeBtn>
				<PanelBody className="d-flex flex-column">
					<AddMeasureForm
						initialValues={selectedMeasure}
						onAdd={addToList}
						onUpdate={editMeasure}
						togglePanel={(event) => toggleAddEditMeasurePanel(event)}
					/>
				</PanelBody>
			</FilterPanel>
		</div>
	) : null;
};

const mapStateToProps = (state) => {
	return {
		ingredientWeightList: get(state.FoodDatabase, "ingredientWeights.results", []),
	};
};

const mapDispatchToProps = { getIngredientWeights, addIngredientWeights, updateIngredientWeights, deleteIngredientWeights };

export default connect(mapStateToProps, mapDispatchToProps)(CommonWeightsAndMeasures);
